import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import Swal from "sweetalert2";
import { api } from "../lib/api";

const getToken = () =>
  localStorage.getItem("token") || localStorage.getItem("agent_token") || "";

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function RemarksPage() {
  const { bil } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const kodKawasan = location.state?.kod_kawasan || null;
  const nomborUnit = location.state?.nombor_unit || null;

  // Remarks
  const [comment, setComment] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [accuracyM, setAccuracyM] = useState(null);

  const [loadingGps, setLoadingGps] = useState(false);
  const [savingRemark, setSavingRemark] = useState(false);

  // PTP
  const [ptpDate, setPtpDate] = useState(todayISO());
  const [ptpAmount, setPtpAmount] = useState("");
  const [ptpNote, setPtpNote] = useState("");
  const [ptpList, setPtpList] = useState([]);
  const [loadingPtp, setLoadingPtp] = useState(false);
  const [savingPtp, setSavingPtp] = useState(false);

  const token = useMemo(() => getToken(), []);

  const fetchGps = async () => {
    setLoadingGps(true);

    if (!navigator.geolocation) {
      setLoadingGps(false);
      await Swal.fire({
        icon: "error",
        title: "GPS tidak disokong",
        text: "Peranti ini tidak menyokong geolocation.",
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude);
        setLng(pos.coords.longitude);
        setAccuracyM(pos.coords.accuracy);
        setLoadingGps(false);
      },
      async (err) => {
        console.error(err);
        setLoadingGps(false);
        await Swal.fire({
          icon: "warning",
          title: "Lokasi gagal",
          text: "Tidak dapat ambil lokasi. Sila benarkan akses lokasi (Location Permission).",
        });
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  };

  const loadPtp = async () => {
    if (!token) return;
    if (!bil) return;

    try {
      setLoadingPtp(true);
      const res = await api.get("/api/agent/ptp", { params: { bil } });
      setPtpList(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      // silent fail to avoid blocking remarks
    } finally {
      setLoadingPtp(false);
    }
  };

  useEffect(() => {
    fetchGps();
    loadPtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!photo) {
      setPhotoPreview("");
      return;
    }
    const url = URL.createObjectURL(photo);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const submitRemark = async (e) => {
    e.preventDefault();

    if (!token) {
      await Swal.fire({
        icon: "error",
        title: "Sesi tamat",
        text: "Token tiada. Sila login semula.",
      });
      return;
    }

    if (!bil) {
      await Swal.fire({
        icon: "error",
        title: "BIL tiada",
        text: "BIL tidak dijumpai.",
      });
      return;
    }

    if (!photo) {
      await Swal.fire({
        icon: "warning",
        title: "Gambar diperlukan",
        text: "Sila ambil / pilih gambar unit sebelum submit.",
      });
      return;
    }

    setSavingRemark(true);
    try {
      const fd = new FormData();
      fd.append("bil", bil);

      if (kodKawasan) fd.append("kod_kawasan", kodKawasan);
      if (nomborUnit) fd.append("nombor_unit", nomborUnit);

      fd.append("comment", comment || "");

      if (lat != null) fd.append("lat", String(lat));
      if (lng != null) fd.append("lng", String(lng));
      if (accuracyM != null) fd.append("accuracy_m", String(accuracyM));

      fd.append("captured_at", new Date().toISOString());
      fd.append("photo", photo);

      await api.post("/api/agent/remarks", fd);

      await Swal.fire({
        icon: "success",
        title: "Berjaya!",
        text: "Remarks telah dihantar.",
        timer: 1400,
        showConfirmButton: false,
      });

      // reset only remark form
      setComment("");
      setPhoto(null);
      setPhotoPreview("");
    } catch (err) {
      console.error(err);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: err?.response?.data?.error || "Gagal submit remarks.",
      });
    } finally {
      setSavingRemark(false);
    }
  };

  const submitPtp = async (e) => {
    e.preventDefault();

    if (!token) {
      await Swal.fire({
        icon: "error",
        title: "Sesi tamat",
        text: "Token tiada. Sila login semula.",
      });
      return;
    }

    if (!bil) {
      await Swal.fire({
        icon: "error",
        title: "BIL tiada",
        text: "BIL tidak dijumpai.",
      });
      return;
    }

    if (!ptpDate) {
      await Swal.fire({
        icon: "warning",
        title: "Tarikh diperlukan",
        text: "Sila pilih tarikh PTP (janji bayar).",
      });
      return;
    }

    setSavingPtp(true);
    try {
      const payload = {
        bil,
        promised_date: ptpDate,
        promised_amount: ptpAmount ? String(ptpAmount).trim() : null,
        note: ptpNote ? String(ptpNote).trim() : null,
      };

      await api.post("/api/agent/ptp", payload);

      await Swal.fire({
        icon: "success",
        title: "PTP disimpan",
        text: "Tarikh janji bayar telah direkodkan.",
        timer: 1400,
        showConfirmButton: false,
      });

      setPtpAmount("");
      setPtpNote("");
      await loadPtp();
    } catch (err) {
      console.error(err);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: err?.response?.data?.error || "Gagal simpan PTP.",
      });
    } finally {
      setSavingPtp(false);
    }
  };

  const formatPtpStatus = (s) => {
    if (!s) return "-";
    return String(s).toUpperCase();
  };

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto bg-white shadow-md rounded-lg p-6 mt-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold mb-1 text-gray-700">Remarks</h2>
            <p className="text-sm text-gray-500">BIL: {bil}</p>
            {(kodKawasan || nomborUnit) && (
              <p className="text-xs text-gray-500 mt-1">
                {kodKawasan ? `Kod Kawasan: ${kodKawasan}` : ""}{" "}
                {nomborUnit ? `| Unit: ${nomborUnit}` : ""}
              </p>
            )}
          </div>

          <button
            type="button"
            className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg"
            onClick={() => navigate(-1)}
            disabled={savingRemark || savingPtp}
          >
            Back
          </button>
        </div>

        {/* ========= Remarks Section ========= */}
        <div className="mt-6 border rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-700 mb-3">Gambar & Catatan</h3>

          <form onSubmit={submitRemark} className="space-y-4">
            <div>
              <label className="block text-gray-600 text-sm mb-2">Gambar Unit</label>

              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                className="block w-full text-sm"
              />

              {photo && (
                <p className="text-xs text-gray-500 mt-1">Selected: {photo.name}</p>
              )}

              {photoPreview && (
                <div className="mt-3">
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="w-full rounded-lg border"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-gray-600 text-sm mb-2">Catatan</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="border rounded-md w-full p-2 text-sm"
                rows={4}
                placeholder="Tulis komen…"
              />
            </div>

            <div className="bg-gray-50 rounded-md p-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-semibold">GPS</span>

                <div className="flex gap-2 items-center">
                  <span className="text-gray-800">
                    {loadingGps ? "Getting..." : lat && lng ? "OK" : "Not available"}
                  </span>
                  <button
                    type="button"
                    onClick={fetchGps}
                    className="px-3 py-1 rounded-md bg-blue-600 text-white text-xs hover:bg-blue-700"
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div className="text-xs text-gray-600 mt-2">
                <div>Lat: {lat ?? "-"}</div>
                <div>Lng: {lng ?? "-"}</div>
                <div>Accuracy (m): {accuracyM ?? "-"}</div>
              </div>
            </div>

            <button
              type="submit"
              disabled={savingRemark}
              className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {savingRemark ? "Submitting..." : "Submit Remarks"}
            </button>
          </form>
        </div>

        {/* ========= PTP Section ========= */}
        <div className="mt-6 border rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-700 mb-3">PTP (Promise To Pay)</h3>

          <form onSubmit={submitPtp} className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Tarikh Janji Bayar</label>
              <input
                type="date"
                className="border rounded-md w-full p-2 text-sm"
                value={ptpDate}
                onChange={(e) => setPtpDate(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Jumlah Janji Bayar (optional)
              </label>
              <input
                type="number"
                step="0.01"
                className="border rounded-md w-full p-2 text-sm"
                value={ptpAmount}
                onChange={(e) => setPtpAmount(e.target.value)}
                placeholder="Contoh: 120.00"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Nota (optional)</label>
              <textarea
                className="border rounded-md w-full p-2 text-sm"
                rows={3}
                value={ptpNote}
                onChange={(e) => setPtpNote(e.target.value)}
                placeholder="Contoh: bayar lepas gaji..."
              />
            </div>

            <button
              type="submit"
              disabled={savingPtp}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {savingPtp ? "Saving..." : "Save PTP"}
            </button>
          </form>

          {/* PTP List */}
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-700">History</h4>
              <button
                type="button"
                className="text-xs px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300"
                onClick={loadPtp}
                disabled={loadingPtp}
              >
                {loadingPtp ? "Loading..." : "Refresh"}
              </button>
            </div>

            {ptpList.length === 0 ? (
              <p className="text-xs text-gray-500 mt-2">
                Tiada rekod PTP untuk BIL ini.
              </p>
            ) : (
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-sm border">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2 border">Tarikh</th>
                      <th className="text-left p-2 border">Jumlah</th>
                      <th className="text-left p-2 border">Status</th>
                      <th className="text-left p-2 border">Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ptpList.map((p) => (
                      <tr key={p.id} className="border-t">
                        <td className="p-2 border">{p.promisedDate || p.promised_date || "-"}</td>
                        <td className="p-2 border">{p.promisedAmount || p.promised_amount || "-"}</td>
                        <td className="p-2 border">{formatPtpStatus(p.status)}</td>
                        <td className="p-2 border">{p.note || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>
    </MainLayout>
  );
}
